<div class="om-page">
  <app-project-nav></app-project-nav>
  <app-page-header
    [title]="library()?.name ?? ('objectMappings.detail.titleFallback' | translate)"
    [subtitle]="library()?.engineId ?? ''"
  >
    <ng-container right>
      @if (!isProjectReader()) {
        <button mat-flat-button type="button" data-testid="om-entry-add" (click)="openEntryDialog()">
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'objectMappings.detail.actions.addEntry' | translate }}
        </button>
        @if (recordable()) {
          <button mat-stroked-button type="button" data-testid="om-record" (click)="recordDialogOpen.set(true)">
            <mat-icon aria-hidden="true">radio_button_checked</mat-icon>
            {{ 'objectMappings.detail.actions.record' | translate }}
          </button>
        }
        <button
          mat-stroked-button
          type="button"
          data-testid="om-rename-library"
          [disabled]="!library()"
          (click)="openRename()"
        >
          <mat-icon aria-hidden="true">drive_file_rename_outline</mat-icon>
          {{ 'objectMappings.detail.actions.renameLibrary' | translate }}
        </button>
        <button
          mat-stroked-button
          type="button"
          class="om-library-delete"
          data-testid="om-library-delete"
          [disabled]="!library()"
          (click)="deleteLibrary()"
        >
          <mat-icon aria-hidden="true">delete</mat-icon>
          {{ 'objectMappings.detail.actions.deleteLibrary' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  @if (pendingCaptures() > 0) {
    <p class="om-review-pending" role="status" data-testid="om-review-pending">
      <mat-icon aria-hidden="true">fact_check</mat-icon>
      <span>{{ pendingCapturesKey() | translate: { count: pendingCaptures() } }}</span>
      <a mat-stroked-button [routerLink]="reviewLink">{{ 'objectMappings.detail.pendingCapturesLink' | translate }}</a>
    </p>
  }

  @if (notice(); as message) {
    <p class="om-hint om-hint--notice" role="status" data-testid="om-notice">{{ message }}</p>
  }

  <div class="om-toolbar">
    <app-search-bar
      class="om-search"
      data-testid="om-entry-search"
      [placeholder]="'objectMappings.detail.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <div
      class="om-state-filter"
      role="group"
      data-testid="om-state-filter"
      [attr.aria-label]="'objectMappings.state.filterLabel' | translate"
    >
      @for (chip of stateChips(); track chip.key) {
        <app-filter-chip
          [attr.data-testid]="'om-chip-' + chip.key"
          [label]="'objectMappings.state.chip.' + chip.key | translate"
          [count]="chip.count"
          [pill]="true"
          [active]="chip.active"
          (clicked)="selectState(chip.state)"
        />
      }
    </div>
    @if (!loading() && state() === 'ok') {
      <span class="om-count" role="status">
        {{ 'objectMappings.detail.shownCount' | translate: { shown: rows().length, total: total() } }}
      </span>
    }
  </div>

  @if (loading()) {
    <div class="om-loading" data-testid="om-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (state() === 'notFound') {
    <p class="om-hint om-hint--error" role="alert" data-testid="om-not-found">
      {{ 'objectMappings.detail.notFound' | translate }}
    </p>
  } @else if (state() === 'error') {
    <div class="om-hint om-hint--error" role="alert" data-testid="om-error">
      <p>{{ 'objectMappings.detail.loadError' | translate }}</p>
      <button mat-stroked-button type="button" data-testid="om-retry" (click)="retry()">
        {{ 'objectMappings.retry' | translate }}
      </button>
    </div>
  } @else if (rows().length === 0) {
    @if (entryState()) {
      <p class="om-hint" data-testid="om-empty-filtered">{{ 'objectMappings.state.emptyFiltered' | translate }}</p>
      <ng-container *ngTemplateOutlet="neverHitHint" />
    } @else if (searchTerm().trim()) {
      <p class="om-hint" data-testid="om-empty-filtered">{{ 'objectMappings.detail.emptyFiltered' | translate }}</p>
      <ng-container *ngTemplateOutlet="neverHitHint" />
    } @else {
      <p class="om-hint" data-testid="om-empty">{{ 'objectMappings.detail.empty' | translate }}</p>
    }
  } @else {
    <div class="om-card" role="table" [attr.aria-label]="'objectMappings.detail.tableLabel' | translate">
      <div class="om-table-header" role="row">
        <div class="om-col om-col--toggle" role="columnheader"></div>
        <div class="om-col om-col--name" role="columnheader">
          {{ 'objectMappings.detail.table.name' | translate }}
        </div>
        <div class="om-col om-col--type" role="columnheader">
          {{ 'objectMappings.detail.table.type' | translate }}
        </div>
        <div class="om-col om-col--best" role="columnheader">
          {{ 'objectMappings.detail.table.bestCandidate' | translate }}
        </div>
        <div class="om-col om-col--last-hit" role="columnheader">
          {{ 'objectMappings.state.lastHit' | translate }}
        </div>
        <div class="om-col om-col--actions" role="columnheader">
          <span class="sr-only">{{ 'objectMappings.detail.table.actions' | translate }}</span>
        </div>
      </div>
      @for (row of rows(); track row.id) {
        <div class="om-row" role="row" data-testid="om-entry">
          <div class="om-col om-col--toggle" role="cell">
            @if (row.candidates.length > 0) {
              <button
                type="button"
                class="om-toggle"
                data-testid="om-toggle"
                [attr.aria-expanded]="isExpanded(row.id)"
                [attr.aria-controls]="'om-candidates-' + row.id"
                [attr.aria-label]="
                  (isExpanded(row.id) ? 'objectMappings.detail.collapse' : 'objectMappings.detail.expand') | translate
                "
                (click)="toggle(row.id)"
              >
                <mat-icon aria-hidden="true">{{ isExpanded(row.id) ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
            }
          </div>
          <div class="om-col om-col--name" role="cell">
            <span class="om-name" data-testid="om-name">{{ row.logicalName }}</span>
            @if (row.missingSince; as since) {
              <app-status-pill
                data-testid="om-missing"
                kind="inaktiv"
                size="sm"
                [label]="'objectMappings.state.missingSince' | translate: { date: since }"
              />
            }
          </div>
          <div class="om-col om-col--type" role="cell">{{ row.typeName }}</div>
          <div class="om-col om-col--best" role="cell" data-testid="om-best">
            @if (row.candidates[0]; as best) {
              <span class="om-strategy">{{ best.strategy }}</span>
              <code class="om-locator">{{ best.value }}</code>
            } @else {
              <span class="om-none">{{ 'objectMappings.detail.noCandidates' | translate }}</span>
            }
          </div>
          <div class="om-col om-col--last-hit" role="cell" data-testid="om-last-hit">
            @if (row.lastHit; as hit) {
              @let hitText =
                ('objectMappings.state.hit' | translate: { when: hit.when, index: hit.index }) +
                (hit.kind ? ' (' + ('objectMappings.state.kind.' + hit.kind | translate) + ')' : '');
              @if (hit.warning) {
                @let tip = hit.warning | translate;
                <span
                  class="om-last-hit om-last-hit--warn"
                  tabindex="0"
                  [matTooltip]="tip"
                  [attr.aria-label]="hitText + ': ' + tip"
                >
                  {{ hitText }}
                </span>
              } @else {
                <span class="om-last-hit">{{ hitText }}</span>
              }
            } @else {
              <span class="om-none">{{ 'objectMappings.state.never' | translate }}</span>
            }
          </div>
          <div class="om-col om-col--actions" role="cell">
            <button
              type="button"
              class="om-action"
              data-testid="om-usages"
              [attr.aria-label]="'objectMappings.detail.actions.usages' | translate: { name: row.logicalName }"
              (click)="showUsages(row)"
            >
              <mat-icon aria-hidden="true">link</mat-icon>
            </button>
            @if (!isProjectReader()) {
              <button
                type="button"
                class="om-action om-action--accent"
                data-testid="om-entry-edit"
                [attr.aria-label]="'objectMappings.detail.actions.edit' | translate: { name: row.logicalName }"
                (click)="openEntryDialog(row)"
              >
                <mat-icon aria-hidden="true">edit</mat-icon>
              </button>
              <button
                type="button"
                class="om-action om-action--accent"
                data-testid="om-rename"
                [attr.aria-label]="'objectMappings.detail.actions.rename' | translate: { name: row.logicalName }"
                (click)="openRename(row)"
              >
                <mat-icon aria-hidden="true">drive_file_rename_outline</mat-icon>
              </button>
              <button
                type="button"
                class="om-action om-action--danger"
                data-testid="om-delete"
                [attr.aria-label]="'objectMappings.detail.actions.delete' | translate: { name: row.logicalName }"
                (click)="deleteEntry(row)"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
        </div>
        @if (isExpanded(row.id)) {
          <div class="om-row om-row--candidates" role="row">
            <div class="om-col om-col--candidates" role="cell">
              <ol
                class="om-candidates"
                data-testid="om-candidates"
                [id]="'om-candidates-' + row.id"
                [attr.aria-label]="'objectMappings.detail.candidatesLabel' | translate"
              >
                @for (candidate of row.candidates; track $index) {
                  <li class="om-candidate">
                    <span class="om-strategy">{{ candidate.strategy }}</span>
                    <code class="om-locator">{{ candidate.value }}</code>
                    @if (candidate.extras) {
                      <span class="om-extras">{{ candidate.extras }}</span>
                    }
                  </li>
                }
              </ol>
            </div>
          </div>
        }
      }
    </div>

    @if (moreError()) {
      <p class="om-hint om-hint--error" role="alert" data-testid="om-more-error">
        {{ 'objectMappings.detail.loadMoreError' | translate }}
      </p>
    }
    @if (canLoadMore()) {
      <div class="om-more">
        <button
          mat-stroked-button
          type="button"
          data-testid="om-load-more"
          [disabled]="loadingMore()"
          [attr.aria-busy]="loadingMore()"
          (click)="loadMore()"
        >
          {{ (loadingMore() ? 'objectMappings.detail.loadingMore' : 'objectMappings.detail.loadMore') | translate }}
        </button>
      </div>
    }
    <ng-container *ngTemplateOutlet="neverHitHint" />
  }
</div>

<ng-template #neverHitHint>
  <p class="om-footnote" data-testid="om-never-hit-hint">{{ 'objectMappings.state.neverHitHint' | translate }}</p>
</ng-template>

@if (renameTarget(); as target) {
  <app-object-rename-dialog
    [projectId]="projectId"
    [mappingId]="mappingId"
    [entryId]="target.entryId"
    [currentName]="target.name"
    (renamed)="onRenamed($event)"
    (cancelled)="renameTarget.set(null)"
  />
}

@if (recordDialogOpen() && library(); as current) {
  <app-object-mapping-create-dialog
    [projectId]="projectId"
    [library]="current"
    (cancelled)="recordDialogOpen.set(false)"
  />
}

@if (entryDialog(); as target) {
  <app-object-entry-dialog
    [projectId]="projectId"
    [mappingId]="mappingId"
    [entry]="target.entry"
    (saved)="onEntrySaved($event)"
    (cancelled)="entryDialog.set(null)"
  />
}

@if (usagesView(); as view) {
  <app-confirm-dialog
    [visible]="true"
    [wide]="true"
    icon="link"
    confirmColor="primary"
    [showCancel]="false"
    [title]="'objectMappings.detail.usagesTitle' | translate: { name: view.name }"
    [message]="'objectMappings.detail.usagesMessage' | translate"
    [confirmLabel]="'objectMappings.detail.close' | translate"
    [cancelLabel]="'objectMappings.detail.close' | translate"
    (confirmed)="usagesView.set(null)"
    (cancelled)="usagesView.set(null)"
  >
    @if (view.error) {
      <p class="om-hint om-hint--error" role="alert" data-testid="om-usages-error">
        {{ 'objectMappings.detail.usagesError' | translate }}
      </p>
    } @else if (view.usages; as usages) {
      <app-object-usage-list class="om-usages" [usages]="usages" [projectId]="projectId" />
    } @else {
      <div class="om-loading om-loading--inline" data-testid="om-usages-loading" role="status">
        <mat-spinner diameter="24" />
        <span>{{ 'objectMappings.detail.usagesLoading' | translate }}</span>
      </div>
    }
  </app-confirm-dialog>
}

@if (deleteView(); as view) {
  <app-confirm-dialog
    [visible]="true"
    [wide]="true"
    [title]="'objectMappings.detail.deleteTitle' | translate: { name: view.row.logicalName }"
    [message]="
      (view.usages ? 'objectMappings.detail.deleteMessage' : 'objectMappings.detail.deleteQuestion') | translate
    "
    [confirmLabel]="(view.usages ? 'objectMappings.detail.deleteConfirm' : 'objectMappings.detail.delete') | translate"
    [cancelLabel]="'objectMappings.rename.cancel' | translate"
    [busy]="view.busy"
    (confirmed)="confirmDelete()"
    (cancelled)="deleteView.set(null)"
  >
    @if (view.usages; as usages) {
      <p class="om-delete-count" data-testid="om-delete-count">
        <strong>{{ usages.occurrenceCount }}</strong>
        {{ 'objectMappings.detail.deleteOccurrences' | translate }}
      </p>
    }
    @if (view.error) {
      <p class="om-hint om-hint--error" role="alert" data-testid="om-delete-error">{{ view.error }}</p>
    }
    @if (view.usages; as usages) {
      <app-object-usage-list class="om-usages" [usages]="usages" [projectId]="projectId" />
    }
  </app-confirm-dialog>
}

@if (libraryDeleteView(); as view) {
  @let testCaseCount = view.usages?.testCases?.length ?? 0;
  @let moduleCount = view.usages?.modules?.length ?? 0;
  @let testCasesText =
    (testCaseCount === 1
      ? 'objectMappings.detail.deleteLibrary.testCasesOne'
      : 'objectMappings.detail.deleteLibrary.testCasesOther'
    ) | translate: { count: testCaseCount };
  @let modulesText =
    (moduleCount === 1
      ? 'objectMappings.detail.deleteLibrary.modulesOne'
      : 'objectMappings.detail.deleteLibrary.modulesOther'
    ) | translate: { count: moduleCount };
  <app-confirm-dialog
    [visible]="true"
    [wide]="true"
    [title]="'objectMappings.detail.deleteLibrary.title' | translate: { name: library()?.name ?? mappingId }"
    [message]="
      (!view.usages
        ? 'objectMappings.detail.deleteLibrary.question'
        : testCaseCount + moduleCount === 0
          ? 'objectMappings.detail.deleteLibrary.usedInStepsMessage'
          : 'objectMappings.detail.deleteLibrary.usedMessage'
      ) | translate: { testCases: testCasesText, modules: modulesText }
    "
    [confirmLabel]="
      (view.usages ? 'objectMappings.detail.deleteLibrary.confirmAnyway' : 'objectMappings.detail.delete') | translate
    "
    [cancelLabel]="'objectMappings.rename.cancel' | translate"
    [busy]="view.busy"
    (confirmed)="confirmLibraryDelete()"
    (cancelled)="libraryDeleteView.set(null)"
  >
    @if (view.error) {
      <p class="om-hint om-hint--error" role="alert" data-testid="om-library-delete-error">{{ view.error }}</p>
    }
    @if (view.usages; as usages) {
      <app-object-usage-list class="om-usages" [usages]="usages" [projectId]="projectId" />
    }
  </app-confirm-dialog>
}
